import type { LivingNexus, NexusEvent } from "./nexus";

/** Original INYRON symbolic vocabulary, inspired by Egyptian iconography; not historical translations. */
export const AKH_GLYPHS = Object.freeze({
  horus: "𓂀", thoth: "𓏞", ankh: "𓋹",
  maat: "𓏤", obelisk: "𓊽", khepri: "𓆣",
} as const);
export type AkhGlyph = keyof typeof AKH_GLYPHS;
export type AkhIntent = "observe" | "trace" | "life" | "certify" | "integrate" | "evolve";
export type AkhState = "proposed" | "simulated" | "certified" | "approved" | "executed" | "denied";
export type AkhEvidence = {
  kind: "test" | "review" | "artifact";
  ref: string;
  passed: boolean;
  atMs: number;
  sha256?: string;
};
export interface AkhInscription {
  version: 1;
  id: string;
  gameId: string;
  worldId: string;
  atMs: number;
  actor: string;
  glyph: AkhGlyph;
  intent: AkhIntent;
  operation: string;
  scope: string;
  targetId?: string;
  payload?: unknown;
  requires: string[];
  causeIds: string[];
}
export type AkhProposal = Omit<AkhInscription, "version" | "id" | "gameId" | "worldId" | "atMs" | "glyph">;
export interface AkhAuditEntry {
  sequence: number;
  messageId: string;
  atMs: number;
  state: AkhState;
  actor: string;
  note: string;
  result?: unknown;
  evidence?: AkhEvidence[];
  previousSha256: string;
  sha256: string;
}
export interface AkhSnapshot {
  version: 1;
  gameId: string;
  worldId: string;
  serial: number;
  anchorSha256: string;
  inscriptions: AkhInscription[];
  ledger: AkhAuditEntry[];
}
export type AkhHandler = {
  simulate: (message: AkhInscription) => Promise<unknown> | unknown;
  execute: (message: AkhInscription) => Promise<unknown> | unknown;
  requires?: string[];
};
export interface AkhPolicy {
  /** Host must authenticate actor and check privileges. Default: all approvals denied. */
  canApprove?: (message: AkhInscription, actor: string) => Promise<boolean> | boolean;
  /** Host must verify evidence from real test infrastructure. Default: no certification. */
  verifyEvidence?: (message: AkhInscription, evidence: AkhEvidence[]) => Promise<boolean> | boolean;
}
export interface AkhOptions {
  gameId: string;
  worldId: string;
  nexus?: LivingNexus;
  policy?: AkhPolicy;
  maxInscriptions?: number;
  maxLedgerEntries?: number;
}
const INTENT_GLYPHS: Record<AkhIntent, AkhGlyph> = {
  observe: "horus", trace: "thoth", life: "ankh",
  certify: "maat", integrate: "obelisk", evolve: "khepri",
};
const OPERATION = /^[a-z][a-z0-9-]*(?:\.[a-z][a-z0-9-]*)+$/;
const CAPABILITY = /^[a-z][a-z0-9:-]*$/;
const SHA256_ZERO = "0".repeat(64);
const MAX_ENVELOPE_BYTES = 8192;
const clone = <T>(value: T): T => {
  const raw = JSON.stringify(value);
  if (typeof raw !== "string" || new TextEncoder().encode(raw).length > MAX_ENVELOPE_BYTES) {
    throw new Error("AKH payload exceeds the safe JSON envelope budget");
  }
  return JSON.parse(raw) as T;
};
const digest = async (text: string): Promise<string> => {
  if (!globalThis.crypto?.subtle) throw new Error("AKH requires secure Web Crypto SHA-256");
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes), value => value.toString(16).padStart(2, "0")).join("");
};

/**
 * Typed, permission-gated event-sourced protocol.
 * An inscription NEVER becomes an arbitrary shell command or a Git operation.
 * The host must explicitly register a handler, verify evidence and authorize execution.
 */
export class AkhProtocol {
  readonly gameId: string;
  readonly worldId: string;
  private readonly nexus?: LivingNexus;
  private readonly policy: AkhPolicy;
  private readonly handlers = new Map<string, AkhHandler>();
  private readonly grants = new Set<string>();
  private readonly maxInscriptions: number;
  private readonly maxLedgerEntries: number;
  private inscriptions: AkhInscription[] = [];
  private ledger: AkhAuditEntry[] = [];
  private serial = 0;
  private sequence = 0;
  private anchorSha256 = SHA256_ZERO;
  private tail: Promise<unknown> = Promise.resolve();
  private readonly seenNexusEvents = new Set<string>();
  /** A persisted approval is history, never transferable permission to execute. */
  private readonly restoredIds = new Set<string>();

  constructor(options: AkhOptions) {
    if (!options.gameId || !options.worldId) throw new Error("AKH requires game and world identity");
    this.gameId = options.gameId;
    this.worldId = options.worldId;
    this.nexus = options.nexus;
    this.policy = options.policy ?? {};
    this.maxInscriptions = Math.max(8, Math.min(512, Math.trunc(options.maxInscriptions ?? 128)));
    this.maxLedgerEntries = Math.max(16, Math.min(1024, Math.trunc(options.maxLedgerEntries ?? 256)));
  }

  register(operation: string, handler: AkhHandler): () => void {
    if (!OPERATION.test(operation) || typeof handler?.simulate !== "function"
      || typeof handler?.execute !== "function") throw new Error("AKH handler requires safe operation and both phases");
    if (this.handlers.has(operation)) throw new Error("AKH duplicate handler: " + operation);
    this.handlers.set(operation, handler);
    return () => { if (this.handlers.get(operation) === handler) this.handlers.delete(operation); };
  }

  /** Granted capabilities only affect registered handlers; they are NOT host authentication. */
  grant(capability: string): void {
    if (!CAPABILITY.test(capability)) throw new Error("AKH invalid capability");
    this.grants.add(capability);
  }
  revoke(capability: string): void { this.grants.delete(capability); }

  propose(input: AkhProposal, atMs = Date.now()): Promise<AkhInscription> {
    return this.transact(async () => {
      if (!input.actor || !input.scope || !OPERATION.test(input.operation)
        || !INTENT_GLYPHS[input.intent] || !Number.isFinite(atMs)
        || (input.requires ?? []).some(cap => !CAPABILITY.test(cap))) {
        throw new Error("AKH invalid inscription");
      }
      if ((input.requires ?? []).length > 12 || (input.causeIds ?? []).length > 24) {
        throw new Error("AKH inscription exceeds dependency budget");
      }
      const inscription = clone({
        ...input, version: 1 as const, id: this.gameId + ":" + this.worldId + ":" + (++this.serial),
        gameId: this.gameId, worldId: this.worldId, atMs: Math.round(atMs),
        glyph: INTENT_GLYPHS[input.intent],
        requires: [...new Set(input.requires ?? [])],
        causeIds: [...new Set(input.causeIds ?? [])],
      }) as AkhInscription;
      this.inscriptions.push(inscription);
      if (this.inscriptions.length > this.maxInscriptions) this.inscriptions.shift();
      await this.append(inscription.id, "proposed", inscription.actor, "registered");
      return clone(inscription);
    });
  }

  get(id: string): AkhInscription | undefined {
    const found = this.inscriptions.find(item => item.id === id);
    return found ? clone(found) : undefined;
  }
  state(id: string): AkhState | undefined {
    return [...this.ledger].reverse().find(item => item.messageId === id && item.state !== "denied")?.state;
  }
  recent(): AkhAuditEntry[] { return JSON.parse(JSON.stringify(this.ledger)) as AkhAuditEntry[]; }

  simulate(id: string, actor = "akh-simulator"): Promise<unknown> {
    return this.transact(async () => {
      const item = this.require(id);
      if (this.state(id) !== "proposed") throw new Error("AKH simulation requires proposed inscription");
      const handler = this.handlers.get(item.operation);
      if (!handler) throw new Error("AKH operation has no registered simulator");
      try {
        const result = clone(await handler.simulate(clone(item)));
        await this.append(id, "simulated", actor, "sandbox-result", result);
        return clone(result);
      } catch (error) {
        await this.append(id, "denied", actor, "simulation-error");
        throw error;
      }
    });
  }

  certify(id: string, evidence: AkhEvidence[], actor = "maat"): Promise<void> {
    return this.transact(async () => {
      const item = this.require(id);
      if (this.state(id) !== "simulated" || !Array.isArray(evidence)
        || evidence.length === 0 || evidence.some(e => !e.passed || !e.ref || !Number.isFinite(e.atMs))) {
        throw new Error("AKH certification requires simulation and passing evidence");
      }
      if (!this.policy.verifyEvidence || !(await this.policy.verifyEvidence(clone(item), clone(evidence)))) {
        await this.append(id, "denied", actor, "unverified-evidence");
        throw new Error("AKH evidence is not independently verified");
      }
      await this.append(id, "certified", actor, "verified-evidence", undefined, clone(evidence));
    });
  }

  approve(id: string, actor: string): Promise<void> {
    return this.transact(async () => {
      const item = this.require(id);
      if (this.state(id) !== "certified" || !actor || !this.policy.canApprove
        || !(await this.policy.canApprove(clone(item), actor))) {
        await this.append(id, "denied", actor || "unknown", "approval-denied");
        throw new Error("AKH trusted approval is required");
      }
      await this.append(id, "approved", actor, "host-approved");
    });
  }

  execute(id: string, actor = "akh-executor"): Promise<unknown> {
    return this.transact(async () => {
      const item = this.require(id);
      if (this.restoredIds.has(id)) {
        if (!this.ledger.some(entry => entry.messageId === id && entry.note === "restored-approval-untrusted")) {
          await this.append(id, "denied", actor, "restored-approval-untrusted");
        }
        throw new Error("AKH restored inscriptions are audit-only; create a new verified and approved proposal");
      }
      const executed = [...this.ledger].reverse().find(e => e.messageId === id && e.state === "executed");
      if (executed) return clone(executed.result);
      const handler = this.handlers.get(item.operation);
      const required = [...new Set([...(item.requires ?? []), ...(handler?.requires ?? [])])];
      if (this.state(id) !== "approved" || !handler || !required.every(cap => this.grants.has(cap))) {
        await this.append(id, "denied", actor, "unapproved-or-missing-capability");
        throw new Error("AKH denied unapproved or unauthorized execution");
      }
      if (this.ledger.some(entry => entry.messageId === id && entry.note === "execution-failed")) {
        throw new Error("AKH ambiguous execution outcome requires manual recovery");
      }
      try {
        const result = clone((await handler.execute(clone(item))) ?? null);
        await this.append(id, "executed", actor, "handler-completed", result);
        return clone(result);
      } catch (error) {
        await this.append(id, "denied", actor, "execution-failed");
        throw error;
      }
    });
  }

  /** Observation-only adapter. Never executes code as a consequence of an event. */
  observeNexus(event: NexusEvent): Promise<AkhInscription | undefined> {
    if (!event || event.type.startsWith("akh.") || this.seenNexusEvents.has(event.id)) return Promise.resolve(undefined);
    const relevant = /(?:error|failure|match-complete|offline-catchup|phase-transition|life-death)/i.test(event.type);
    if (!relevant) return Promise.resolve(undefined);
    this.seenNexusEvents.add(event.id);
    if (this.seenNexusEvents.size > 2048) {
      const oldest = this.seenNexusEvents.values().next().value;
      if (oldest) this.seenNexusEvents.delete(oldest);
    }
    return this.propose({
      actor: "horus:" + event.source, intent: "observe",
      operation: "nexus.observe", scope: event.gameId, targetId: event.entityId,
      payload: { eventId: event.id, eventType: event.type, worldId: event.worldId },
      requires: [], causeIds: [event.id],
    }, event.atMs).catch(error => {
      this.seenNexusEvents.delete(event.id);
      throw error;
    });
  }

  snapshot(): AkhSnapshot {
    return {
      version: 1, gameId: this.gameId, worldId: this.worldId, serial: this.serial,
      anchorSha256: this.anchorSha256,
      inscriptions: JSON.parse(JSON.stringify(this.inscriptions)) as AkhInscription[],
      ledger: this.recent(),
    };
  }

  restore(snapshot: unknown): Promise<number> {
    return this.transact(async () => {
      if (!snapshot || typeof snapshot !== "object") return 0;
      const value = snapshot as Partial<AkhSnapshot>;
      if (value.version !== 1 || value.gameId !== this.gameId || value.worldId !== this.worldId
        || !Array.isArray(value.ledger) || !Array.isArray(value.inscriptions)
        || value.ledger.length > this.maxLedgerEntries || value.inscriptions.length > this.maxInscriptions
        || typeof value.anchorSha256 !== "string" || !/^[a-f0-9]{64}$/.test(value.anchorSha256)
        || !Number.isSafeInteger(value.serial) || (value.serial ?? -1) < 0) {
        throw new Error("AKH invalid snapshot");
      }
      let hash = value.anchorSha256;
      let lastSequence = 0;
      for (const row of value.ledger) {
        if (!row || row.previousSha256 !== hash || !Number.isSafeInteger(row.sequence)
          || row.sequence <= lastSequence || (lastSequence && row.sequence !== lastSequence + 1)) {
          throw new Error("AKH ledger sequence or chain mismatch");
        }
        const { sha256: recorded, ...unsigned } = row;
        const calculated = await digest(JSON.stringify(unsigned));
        if (calculated !== recorded) throw new Error("AKH ledger digest mismatch");
        hash = recorded;
        lastSequence = row.sequence;
      }
      const inscriptions = value.inscriptions.map(item => {
        if (item?.version !== 1 || item.gameId !== this.gameId || item.worldId !== this.worldId
          || !OPERATION.test(item.operation) || INTENT_GLYPHS[item.intent] !== item.glyph) {
          throw new Error("AKH invalid restored inscription");
        }
        return clone(item);
      });
      this.inscriptions = inscriptions;
      this.restoredIds.clear();
      for (const item of inscriptions) this.restoredIds.add(item.id);
      this.ledger = JSON.parse(JSON.stringify(value.ledger)) as AkhAuditEntry[];
      this.anchorSha256 = value.anchorSha256;
      this.serial = value.serial!;
      this.sequence = lastSequence;
      this.seenNexusEvents.clear();
      for (const item of inscriptions) {
        if (item.operation === 'nexus.observe') for (const cause of item.causeIds) this.seenNexusEvents.add(cause);
      }
      return this.ledger.length;
    });
  }

  private require(id: string): AkhInscription {
    const found = this.inscriptions.find(item => item.id === id);
    if (!found) throw new Error("AKH unknown inscription: " + id);
    return found;
  }
  private async append(
    id: string, state: AkhState, actor: string, note: string,
    result?: unknown, evidence?: AkhEvidence[],
  ): Promise<void> {
    const previousSha256 = this.ledger.length ? this.ledger[this.ledger.length - 1].sha256 : this.anchorSha256;
    const unsigned = {
      sequence: ++this.sequence, messageId: id, atMs: Date.now(),
      state, actor, note, ...(result === undefined ? {} : { result }),
      ...(evidence === undefined ? {} : { evidence }),
      previousSha256,
    };
    const sha256 = await digest(JSON.stringify(unsigned));
    this.ledger.push({ ...unsigned, sha256 });
    if (this.ledger.length > this.maxLedgerEntries) {
      this.anchorSha256 = this.ledger.shift()!.sha256;
    }
    // Cross-game transport receives metadata only, never an implicit command.
    this.nexus?.publish({
      atMs: unsigned.atMs, gameId: this.gameId, worldId: this.worldId,
      type: "akh.inscription." + state, source: "akh-omega",
      payload: { id, state, sha256 },
    });
  }
  private transact<T>(task: () => Promise<T>): Promise<T> {
    const work = this.tail.then(task, task);
    this.tail = work.then(() => undefined, () => undefined);
    return work;
  }
}